import { nanoid } from 'nanoid'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useReducer } from 'react'
import io from 'socket.io-client'
import { AuthenticationContext } from '../context/authenticationContext'
import { initialState, socketReducer } from '../reducers/socketReducer'
import { delay } from '../utils/utilities'

import {
  ANNOUNCEMENTS_SOCKET_URL,
  GLOBAL_EVENTS,
  LIVESTREAM_EVENTS,
  PRIVATE_MESSAGES_SOCKET_URL,
  SOCKET_ROOMS,
  TRADES_SOCKET_URL
} from '@moal/api'

import {
  R_ERROR,
  R_LOADING,
  R_RESET,
  R_SOCKET
} from '../constants/actions/socketActions'
import { PRODUCT_CODE } from '../utils/ENV'
import { SiteConfigContext } from './siteConfigContext'

export const SocketContext = React.createContext({})

export const SocketProvider = ({ children }) => {
  const { setSettings, activePoll, updateActivePoll } =
    useContext(SiteConfigContext)
  const { user, setFreshUserData } = useContext(AuthenticationContext)
  const [state, dispatch] = useReducer(socketReducer, initialState)

  useEffect(() => {
    if (state.loading) {
      connectToSocket(process.env.REACT_APP_SOCKET_URL, 'socket')
      connectToSocket(TRADES_SOCKET_URL, 'tradesSocket')
      connectToSocket(PRIVATE_MESSAGES_SOCKET_URL, 'privateMessagesSocket')
      if (user) {
        connectToSocket(ANNOUNCEMENTS_SOCKET_URL, 'announcementsSocket')
      }
    }
  }, [user])

  useEffect(() => {
    if (state.socket) {
      state.socket.on(GLOBAL_EVENTS.CONNECT_ERROR, function () {
        dispatch({
          type: R_ERROR,
          payload: 'Failed to connect to server. [code: C]'
        })
      })

      state.socket.on(GLOBAL_EVENTS.ERROR, async function (data) {
        dispatch({ type: R_ERROR, payload: data })
        await delay(5000)
        connectToSocket(process.env.REACT_APP_SOCKET_URL, 'socket')
      })

      state.socket.on(GLOBAL_EVENTS.CONNECT, () => {
        joinRoom(state.socket, SOCKET_ROOMS.LIVESTREAM.USER)
        joinRoom(state.socket, SOCKET_ROOMS.LIVESTREAM.VIEW_COUNT)
      })
      state.socket.on(LIVESTREAM_EVENTS.PUT_USER, handlePutUser)
      state.socket.on(LIVESTREAM_EVENTS.UPDATE_SETTINGS, handleUpdateSettings)
    }
    return () => {
      if (state.socket) {
        state.socket.off(LIVESTREAM_EVENTS.PUT_USER, handlePutUser)
        state.socket.off(
          LIVESTREAM_EVENTS.UPDATE_SETTINGS,
          handleUpdateSettings
        )
      }
    }
  }, [state.socket])

  useEffect(() => {
    if (state.socket) {
      state.socket.on(LIVESTREAM_EVENTS.UPDATE_POLL, handleUpdatePoll)
    }
    return () => {
      if (state.socket) {
        state.socket.off(LIVESTREAM_EVENTS.UPDATE_POLL, handleUpdatePoll)
      }
    }
  }, [state.socket, activePoll])

  useEffect(() => {
    if (state.privateMessagesSocket) {
      state.privateMessagesSocket.emit(GLOBAL_EVENTS.CONNECTED, {
        user
      })

      state.privateMessagesSocket.on(GLOBAL_EVENTS.CONNECT_ERROR, function () {
        dispatch({
          type: R_ERROR,
          payload: 'Failed to connect to server. [code: P]'
        })
      })

      state.privateMessagesSocket.on(
        GLOBAL_EVENTS.ERROR,
        async function (data) {
          dispatch({ type: R_ERROR, payload: data })
          await delay(5000)
          connectToSocket(PRIVATE_MESSAGES_SOCKET_URL, 'privateMessagesSocket')
        }
      )
    }
    return () => {
      if (state.privateMessagesSocket) {
        state.privateMessagesSocket.off(GLOBAL_EVENTS.CONNECTED)
      }
    }
  }, [state.privateMessagesSocket])

  useEffect(() => {
    if (user && state.announcementsSocket) {
      state.announcementsSocket.emit(GLOBAL_EVENTS.CONNECTED, {
        user
      })
    }
  }, [state.announcementsSocket, user])

  useEffect(() => {
    if (state.tradesSocket) {
      // state.tradesSocket.emit(GLOBAL_EVENTS.CONNECTED, {
      //   user
      // })

      state.tradesSocket.on(GLOBAL_EVENTS.CONNECT_ERROR, function () {
        dispatch({
          type: R_ERROR,
          payload: 'Failed to connect to server. [code: T]'
        })
      })

      state.tradesSocket.on(GLOBAL_EVENTS.ERROR, async function (data) {
        dispatch({ type: R_ERROR, payload: data })
        await delay(5000)
        connectToSocket(TRADES_SOCKET_URL, 'tradesSocket')
      })
    }
    return () => {
      if (state.tradesSocket) {
        //   state.tradesSocket.off(GLOBAL_EVENTS.CONNECTED)
      }
    }
  }, [state.tradesSocket])

  const connectToSocket = (url, type) => {
    const newSocket = io.connect(url, {
      transports: ['websocket'],
      auth: {
        user: user?._id,
        token: localStorage.getItem('authToken'),
        nano: getSocketNano(),
        itemNumber: PRODUCT_CODE
      }
    })
    dispatch({ type: R_SOCKET, payload: { type, socket: newSocket } })
    dispatch({ type: R_LOADING, payload: false })
    dispatch({ type: R_ERROR, payload: null })
  }

  const getSocketNano = () => {
    const socketNano = localStorage.getItem('socketNano')
    if (!socketNano) {
      const newNano = nanoid()
      localStorage.setItem('socketNano', newNano)
      return newNano
    }
    return socketNano
  }

  const handlePutUser = (data) => {
    setFreshUserData()
    if (state.socket) state.socket.disconnect()
    if (state.tradesSocket) state.tradesSocket.disconnect()
    if (state.privateMessagesSocket) state.privateMessagesSocket.disconnect()
    if (state.announcementsSocket) state.announcementsSocket.disconnect()
    dispatch({ type: R_RESET, payload: null })
  }

  const handleUpdateSettings = (data) => {
    setSettings(data)
  }

  const handleUpdatePoll = (data) => {
    updateActivePoll(data)
  }

  const joinRoom = (socket, room) => {
    socket.emit(GLOBAL_EVENTS.JOIN_ROOM, {
      user: user,
      room: room
    })
  }

  return (
    <SocketContext.Provider
      value={{
        socket: state.socket,
        socketError: state.error,
        privateMessagesSocket: state.privateMessagesSocket,
        tradesSocket: state.tradesSocket,
        announcementsSocket: state.announcementsSocket,
        joinRoom
      }}
    >
      {children}
    </SocketContext.Provider>
  )
}

SocketProvider.propTypes = {
  children: PropTypes.any
}
